<template>
  <Panel hide-header class="min-h-screen">
    <div class="p-4" v-if="step == 1">
      <div class="flex">
        <div class="text-black font-normal text-3xl flex-grow mb-4">
          New Blast
        </div>
        <div class="text-sm text-cardstock-blue-500 flex flex-col justify-center">
          Step 1 of 3
        </div>
      </div>
      <label class="block text-gray-600 text-sm mb-2">
        Search for Grade Levels, Groups, or Individual Students... (required)
      </label>
      <SearchSelector
        class="mb-8"
        :possibleItems="blastRecipients"
        :multiple="true"
        :search="true"
        :showComboSelections="false"
        :showListSelections="false"
        :showSelectedItemsAsDisabled="true"
        v-model="proposedRecipients"

        :proposedSearchEntry="proposedSearch"
        v-on:search="proposedSearch = $event"
      >
        <template v-slot:menu-item="{item}">
          <div v-if="item.__typename == 'Student'">
            <div>
              <strong>{{item.first}} {{item.last}}</strong>
            </div>
            <div class="mt-2">
              Student
              <span v-if="item.gradeLevel">in {{item.gradeLevel.name}}</span>
            </div>
          </div>
          <div v-else-if="item.__typename == 'Group'">
            <div>
              <strong>{{item.name}}</strong>
            </div>
            <div class="mt-2">
              Group belonging to {{item.teacher.last}} containing {{item.studentCount}} students
            </div>
          </div>
          <div v-else-if="item.__typename == 'GradeLevel'">
            <div>
              <strong>{{item.name}}</strong>
            </div>
            <div class="mt-2">
              Grade Level containing {{item.studentCount}} students
            </div>
          </div>
        </template>
      </SearchSelector>

      <div v-if="filteredProposedStudents.length > 0">
        <label class="block text-gray-600 text-sm mb-2">
          Selected Recipients
        </label>
        <div>
          <div class="flex w-full mb-4 border rounded px-4" v-for="student in filteredProposedStudents" :key="student.id">
            <div class="text-xl w-1/4 mt-4">
              {{ student.last }}, {{ student.first }}
            </div>
            <div class="flex-grow mt-4">
              <label class="flex w-full mb-4" v-for="homeNumber in student.homeNumbers">
                <div class="mx-4 w-1/3 select-none">
                  <input class="mr-2" type="checkbox" :checked="isHomeNumberIncluded(homeNumber)" v-on:input="didCheckHomeNumber(homeNumber)">
                  {{homeNumber.formattedNumber}}
                </div>
                <div class="mx-4 w-1/3 select-none">{{homeNumber.label}}</div>
                <div class="mx-4 w-1/3 select-none text-right" v-if="homeNumber.homeLanguage">
                  {{homeNumber.homeLanguage.name}}
                </div>
                <div v-else class="mx-4 w-1/3 select-none text-right">
                  &nbsp;
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 p-4 sticky bottom-0 flex items-right justify-around relative" >
        <div class="absolute top-0 bottom-0 left-0 white-0 opacity-75 bg-white w-full h-full"></div>
        <button @click="didStartOver" class="text-white py-2 px-4 rounded cursor-pointer bg-gray-600 text-center whitespace-nowrap z-10 h-10">
          Start Over
        </button>
        <ButtonSubmit @click.native="didStepTwo" class="flex-shrink-0 ml-4 h-10 z-10">
          Type Message
        </ButtonSubmit>
      </div>
    </div>

    <div class="p-4" v-else-if="step == 2">
      <div class="flex">
        <div class="text-black font-normal text-3xl flex-grow mb-4">
          New Blast
        </div>
        <div class="text-sm text-cardstock-blue-500 flex flex-col justify-center">
          Step 2 of 3
        </div>
      </div>
      <label class="block text-gray-600 text-sm mb-2">
        Blast Body
      </label>
      <textarea
        class="w-full border border-gray-500 rounded leading-normal h-32 py-2 px-3 flex-grow"
        tabindex="2"
        v-model="proposedOriginalBody"
        name="body"
        v-validate="'max:320'"
        placeholder="Enter your message..."
      ></textarea>
      <div
        :class="{'text-red-700': isTooLong(proposedOriginalBody)}"
        class="text-sm"
      >
        {{characterCount(proposedOriginalBody)}} / 320
      </div>

      <div class="mt-4 p-4 sticky bottom-0 flex items-right justify-around relative" >
        <div class="absolute top-0 bottom-0 left-0 white-0 opacity-75 bg-white w-full h-full"></div>
        <button @click="didStartOver" class="text-white py-2 px-4 rounded cursor-pointer bg-gray-600 text-center whitespace-nowrap z-10 h-10">
          Start Over
        </button>
        <ButtonSubmit @click.native="didStepThree" class="flex-shrink-0 ml-4 h-10 z-10">
          Generate Translations
        </ButtonSubmit>
      </div>
    </div>

    <div class="p-4" v-else-if="step == 3">
      <div class="flex">
        <div class="text-black font-normal text-3xl flex-grow mb-4">
          New Blast
        </div>
        <div class="text-sm text-cardstock-blue-500 flex flex-col justify-center">
          Step 3 of 3
        </div>
      </div>
      <label class="block text-gray-600 text-sm mb-2">
        Original Text
      </label>
      <textarea
        class="w-full border border-gray-500 rounded leading-normal h-32 py-2 px-3 flex-grow bg-gray-200 mb-8"
        tabindex="2"
        v-model="proposedOriginalBody"
        name="body"
        v-validate="'max:320'"
        disabled
      ></textarea>

      <div v-for="homeLanguage in proposedHomeLanguages">
        <div v-if="homeLanguage">
          <div>
            For numbers whose Home Language is {{homeLanguage.name}}
          </div>
          <div>
            <textarea
              class="w-full border border-gray-500 rounded leading-normal h-32 py-2 px-3 flex-grow"
              tabindex="2"
              name="body"
              ref="proposedBody"
              :value="getBody(homeLanguage.id)"
              v-on:input="didChangeBody($event.target.value, homeLanguage.id)"
            ></textarea>
          </div>
        </div>
        <div v-else>
          <div>
            Numbers that have no Home Language set will receive your original message
          </div>
          <textarea
            class="w-full border border-gray-500 rounded leading-normal h-12 py-2 px-3 flex-grow"
            tabindex="2"
            name="body"
            v-validate="'max:320'"
            ref="proposedBody"
            :value="getBody(null)"
            v-on:input="didChangeBody($event.target.value, null)"
          ></textarea>
        </div>
      </div>

      <div class="text-center text-gray-600 mt-4">
        You can revise any of the above bodies before sending.
      </div>

      <div class="mt-4 p-4 sticky bottom-0 flex items-right justify-around relative" >
        <div class="absolute top-0 bottom-0 left-0 white-0 opacity-75 bg-white w-full h-full"></div>
        <button @click="didStartOver" class="text-white py-2 px-4 rounded cursor-pointer bg-gray-600 text-center whitespace-nowrap z-10 h-10">
          Start Over
        </button>
        <ButtonSubmit @click.native="didSubmit" :isSubmitting="isSubmitting" class="flex-shrink-0 ml-4 h-10 z-10">
          Finish & Send
        </ButtonSubmit>
      </div>
    </div>

  </Panel>
</template>

<script>
import _get                    from 'lodash-es/get'
import _map                    from 'lodash-es/map'
import _find                   from 'lodash-es/find'
import _findIndex              from 'lodash-es/findIndex'
import _flatMap                from 'lodash-es/flatMap'
import _filter                 from 'lodash-es/filter'
import _concat                 from 'lodash-es/concat'
import _uniqBy                 from 'lodash-es/uniqBy'
import _sortBy                 from 'lodash-es/sortBy'

import Panel                   from '@/components/Panel.vue'
import SearchSelector          from '@/components/SearchSelector.vue'
import ButtonSubmit            from '@/components/ButtonSubmit.vue'

import TranslationsQuery       from '@/queries/Translations.gql'
import BlastRecipientsQuery    from '@/queries/BlastRecipients.gql'
import BlastCreateMutation     from '@/mutations/BlastCreate.gql'

import { queryHandler }        from '@/responseHandler'

export default {
  components: {
    Panel,
    SearchSelector,
    ButtonSubmit
  },

  props: {
    teamId: {
      required: true
    },
    currentUser: {
      required: true
    }
  },

  data() {
    return {
      excludedHomeNumbers: [],
      hasRecipiented: false,
      selectedStudents: [],
      proposedOriginalBody: '',
      proposedMoods: [],
      proposedRecipients: [],
      proposedSearch: '',
      step: 1,
      bodies: [],
      isSubmitting: false
    }
  },

  computed: {
    blastVariables() {
      var self = this

      var recipients = _map(this.proposedHomeNumbers, (homeNumber) => {
        if (homeNumber.homeLanguage == null) {

          return {
            homeNumberId: homeNumber.id,
            originalText: self.proposedOriginalBody,
            translatedText: null,
            homeLanguageId: null
          }

        } else {

          return {
            homeNumberId: homeNumber.id,
            originalText: self.proposedOriginalBody,
            translatedText: self.getBody(homeNumber.homeLanguage.id),
            homeLanguageId: homeNumber.homeLanguage.id
          }

        }
      })

      return {
        teamId: this.teamId,
        recipients: recipients
      }
    },

    proposedMoodIds() {
      return _map(this.proposedMoods, 'id')
    },

    proposedHomeLanguages() {
      let duplicateLanguages = _map(this.proposedHomeNumbers, 'homeLanguage')
      return _uniqBy(duplicateLanguages, 'id')
    },

    proposedHomeLanguageIds() {
      return _map(this.proposedHomeLanguages, 'id')
    },

    proposedGradeLevelStudents() {
      let gradeLevels = _filter(this.proposedRecipients, { '__typename': "GradeLevel"})
      return _flatMap(gradeLevels, 'students')
    },

    proposedGroupStudents() {
      let groups = _filter(this.proposedRecipients, { '__typename': "Group"})
      return _flatMap(groups, 'students')
    },

    proposedStudents() {
      return _filter(this.proposedRecipients, { '__typename': "Student"})
    },

    filteredProposedStudents() {
      let students = _concat(this.proposedStudents, this.proposedGradeLevelStudents, this.proposedGroupStudents)
      let uniqueStudents = _uniqBy(students, 'id')
      return _sortBy(uniqueStudents, ['last', 'first'])
    },

    proposedHomeNumbers() {
      var self = this

      let rawHomeNumbers = _flatMap(this.filteredProposedStudents, 'homeNumbers')
      let uniqueHomeNumbers = _uniqBy(rawHomeNumbers, 'id')
      let filteredHomeNumbers = _filter(uniqueHomeNumbers, (hn) => {
        return self.isHomeNumberIncluded(hn)
      })

      return filteredHomeNumbers
    }
  },

  methods: {

    characterCount(body) {
      if (body) {
        return body.length
      } else {
        return 0
      }
    },

    didStepThree() {
      this.isTranslating = true

      queryHandler({
        apollo: this.$apollo,
        component: 'BlastNew',
        query: 'TranslationsQuery',
        queryOpts: {
          query: TranslationsQuery,
          variables: {
            original: this.proposedOriginalBody,
            homeLanguageIds: this.proposedHomeLanguageIds
          }
        },
        onSuccess: (data) => {
          this.bodies = data.translations
          this.step = 3
        },
        onErrors: (errors) => {
          this.isTranslating = false
        },
        onError: (error) => {
          this.isTranslating = false
        }
      })
    },

    didSubmit() {
      this.isSubmitting = true
      this.$apollo.mutate({
        mutation: BlastCreateMutation,
        variables: this.blastVariables
      }).then((data) => {
        this.$router.push({name: 'conversations'})
        this.isSubmitting = false
      })
    },

    didChangeBody(newBody, homeLanguageId) {
      let foundIndex = _findIndex(this.bodies, ['homeLanguageId', homeLanguageId])
      this.bodies[foundIndex].translation = newBody
    },

    didStepTwo() {
      this.step = 2
    },

    getBody(homeLanguageId) {
      let body = _find(this.bodies, {'homeLanguageId': homeLanguageId})
      return _get(body, 'translation') || ''
    },

    isTooLong(body) {
      if (body) {
        return body.length > 320
      } else {
        return false
      }
    },

    didCheckHomeNumber(homeNumber) {
      const foundIndex = _findIndex(this.excludedHomeNumbers, ['id', homeNumber.id])

      if (foundIndex == -1) {
        this.excludedHomeNumbers = _concat(this.excludedHomeNumbers, homeNumber)
      } else {
        var newValue = this.excludedHomeNumbers.slice(0)
        newValue.splice(foundIndex, 1)

        this.excludedHomeNumbers = newValue
      }
    },

    didStartOver() {
      this.proposedRecipients = []
      this.excludedHomeNumbers = []
      this.proposedOriginalBody = ''
      this.bodies = {}
      this.step = 1
    },

    isHomeNumberIncluded(homeNumber) {
      const foundIndex = _findIndex(this.excludedHomeNumbers, ['id', homeNumber.id])

      return (foundIndex == -1)
    }
  },

  apollo: {
    blastRecipients: {
      query: BlastRecipientsQuery,
      variables() {
        return {
          search: this.proposedSearch,
          teamId: this.teamId
        }
      },
      fetchPolicy: 'no-cache'
    }
  }
}
</script>
